import type { ElementType, HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/cn";
import type { TypographyRole } from "@/theme";
import styles from "./Text.module.css";

export type TextColor =
  | "primary"
  | "secondary"
  | "muted"
  | "inverse"
  | "link";

const roleClass: Record<TypographyRole, string> = {
  display: styles.display,
  hero: styles.hero,
  h1: styles.h1,
  h2: styles.h2,
  title: styles.title,
  body: styles.body,
  bodyStrong: styles.bodyStrong,
  small: styles.small,
  label: styles.label,
  caption: styles.caption,
  overline: styles.overline,
};

const colorClass: Record<TextColor, string> = {
  primary: styles.primary,
  secondary: styles.secondary,
  muted: styles.muted,
  inverse: styles.inverse,
  link: styles.link,
};

const defaultElement: Partial<Record<TypographyRole, ElementType>> = {
  display: "h1",
  hero: "h1",
  h1: "h1",
  h2: "h2",
  title: "h3",
  overline: "p",
};

export interface TextProps extends HTMLAttributes<HTMLElement> {
  children?: ReactNode;
  role?: TypographyRole;
  color?: TextColor;
  as?: ElementType;
}

export function Text({
  children,
  className,
  role = "body",
  color = "primary",
  as,
  ...rest
}: TextProps) {
  const Component = as ?? defaultElement[role] ?? "p";
  return (
    <Component
      className={cn(styles.root, roleClass[role], colorClass[color], className)}
      {...rest}
    >
      {children}
    </Component>
  );
}
